<template>
    <div>
        <div class="h-[70px] bg-white flex justify-between items-center px-4">
            <div class="flex gap-2">
                <h3>
                    {{ journey.code }}
                </h3>
                <hts-icon-svg
                    v-if="journey.processAlertStatus >= 30"
                    name="warning-sign"
                    class-name="h-7 w-7 -translate-y-2" />
                <hts-icon-svg
                    v-if="journey.totalWaitingTime"
                    name="hourglass"
                    class-name="h-7 w-7 -translate-y-2 fill-yellow-500" />
                <hts-icon-svg
                    v-if="hasDriverAlert"
                    name="user-delete"
                    class="h-7 w-7 -translate-y-2 fill-purple-500" />
            </div>
            <hts-form-button
                color="info"
                icon-name="hi-pencil-icon"
                :outline="false"
                @click="handleEditPlan('plan')">
                {{ $t('labels.planningModify') }}
            </hts-form-button>
        </div>
        <div class="h-full">
            <hts-step-by-step-travel
                :steps="CStages"
                direction="horizontal"
                class="my-20"
                @clickStep="clickStep">
                <template #feltInStep="step: any">
                    <div
                        v-if="journey.processStatus !== 0"
                        :class="{
                            'text-main-text': !step.step.warning,
                            'text-error-500': step.step.warning === 'red',
                            'opacity-50': step.step.disable,
                            'text-sm': step.step.stepType === 'task'
                        }">
                        <p class="text-right font-bold">
                            {{ step.step.time }}
                        </p>
                        <p class="text-right">
                            {{ step.step.day }}
                        </p>
                    </div>
                </template>
                <template #rightInStep="step: any">
                    <div
                        v-if="step.step.stepType === 'node'"
                        :class="{
                            'text-main-text': !step.step.warning,
                            'text-error-500': step.step.warning === 'red',
                            'opacity-50': step.step.disable,
                            'text-sm': step.step.stepType === 'task'
                        }">
                        <div class="flex gap-2 w-full">
                            <p class="font-bold max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap">
                                {{ step.step.nodeLocationName }}
                            </p>
                            <div class="relative">
                                <span class="absolute top-0 left-0 -translate-y-3 flex gap-1">
                                    <div
                                        v-if="step.step.vehicle"
                                        class="w-7 h-7">
                                        <img
                                            class="w-full"
                                            :src="vehicle" >
                                    </div>

                                    <hts-icon-svg
                                        v-if="step.step.waitingTime"
                                        name="hourglass"
                                        class-name="h-7 w-7 fill-yellow-500" />

                                    <hts-icon-svg
                                        v-if="step.step.haveDocument"
                                        name="document"
                                        class-name="h-7 w-7 fill-gray-100 " />
                                </span>
                            </div>
                        </div>

                        <p>
                            {{ step.step.locationName }}
                        </p>
                    </div>
                    <div
                        v-if="step.step.stepType === 'task'"
                        class="text-sm"
                        :class="{
                            'text-main-text': step.step.warning === '',
                            'text-error-500': step.step.warning === 'red',
                            'opacity-50': step.step.disable
                        }">
                        <div class="flex gap-2 w-full">
                            <p class="font-bold max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap">
                                {{ step.step.name }}
                            </p>
                            <div class="relative">
                                <span class="absolute top-0 left-0 -translate-y-3 flex gap-1">
                                    <hts-icon-svg
                                        v-if="step.step.waitingTime"
                                        name="hourglass"
                                        class-name="h-7 w-7 fill-yellow-500" />
                                </span>
                            </div>
                        </div>

                        <p
                            v-for="time in step.step.timeConditions"
                            :key="time"
                            :class="{
                                'text-success-500': time.timeType === 'EARLIEST',
                                'text-warning-500': time.timeType === 'LATEST'
                            }">
                            {{ time.label }}
                        </p>
                    </div>
                </template>
            </hts-step-by-step-travel>
        </div>
    </div>
</template>

<script setup lang="ts">
const { $location } = useNuxtApp()
const { $formatters } = useNuxtApp()
const vehicle = ref('/assets/vehicle.png')
const apiDocument = $url.monitoring.journey.getDocuments
const props = defineProps({
  journey: {
    type: Object,
    required: true
  }
})

const emits = defineEmits(['modify'])
const { journey } = toRefs(props)
const journeyDocuments = ref<{ [key: string]: any }[]>([])

onBeforeMount(async () => {
  await getJourneyDocuments()
})

const getJourneyDocuments = async () => {
  if (!apiDocument) {
    return
  }

  const { data }: any = await $api(apiDocument, { uriParams: { ID: journey.value._id } })
  const temp = (data?.value || {}).data || []
  journeyDocuments.value = temp
}

const nodeShowTasks = ref([] as string[])
const journeyStartAtActualValue = computed(() => journey.value.startAtActual || journey.value.startAtEstimate)
const journeyStopAtActualValue = computed(() => journey.value.stopAtActual || journey.value.stopAtActualEstimate)

const cStartAtLocation = computed(() => {
  const location = $location.getLocation(journey.value?.startAtLocationId)

  return {
    nodeLocationName: location.locationName,
    locationName: location.address,
    stepType: 'node',
    time: $formatters.datetime(journeyStartAtActualValue.value, 'HH:mm'),
    day: $formatters.datetime(journeyStartAtActualValue.value, 'DD/MM/YYYY'),
    disable: journey.value.processStatus !== 0,
    vehiclePassed: true
  }
})

const cStopAtLocation = computed(() => {
  const location = $location.getLocation(journey.value?.stopAtLocationId)

  return {
    nodeLocationName: location.locationName,
    locationName: location.address,
    stepType: 'node',
    time: $formatters.datetime(journeyStopAtActualValue.value, 'HH:mm'),
    day: $formatters.datetime(journeyStopAtActualValue.value, 'DD/MM/YYYY'),
    estimate: !journey.value.stopAtActual,
    disable: !!journey.value.stopAtActual
  }
})

const travelDetails = computed(() => journey.value.travelDetails)
const stagesHaveDocument = computed(() => {
  return journeyDocuments.value.map((item: any) => item.positionId)
})
const hasDriverAlert = computed(() => {
  return Array.isArray(journey.value?.alerts) && journey.value?.driverAlerts?.filter((a: any) => !a.skipped).length
})

const getTravelDetail = (type: 'stage' | 'task', stageIndex: number, taskIndex?: number) => {
  const stage = travelDetails.value.find((item: any) => item.index === stageIndex && item.type === 'stage')

  if (type === 'stage') {
    return stage
  }

  if (type === 'task') {
    if (typeof taskIndex !== 'number') {
      return
    }

    return travelDetails.value.find((item: any) => item.stageIndex === stage.stageIndex && item.index === taskIndex && item.type === type)
  }
}

const getWarning = (type: 'stage' | 'task', stageIndex: number, taskIndex?: number) => {
  const stage = travelDetails.value.find((item: any) => item.index === stageIndex && item.type === 'stage')
  let processAlertStatus = 0
  if (type === 'stage') {
    const tmp = travelDetails.value.filter((item: any) => item.stageIndex === stage.stageIndex && item.type === 'task')
    if (tmp) {
      tmp.forEach((item: any) => {
        if (item.processAlertStatus > processAlertStatus) {
          processAlertStatus = item.processAlertStatus
        }
      })
    }
  }

  if (type === 'task') {
    if (typeof taskIndex === 'number') {
      const tmp = travelDetails.value.find((item: any) => item.stageIndex === stage.stageIndex && item.index === taskIndex && item.type === 'task')

      if (tmp) {
        if (tmp.processAlertStatus > processAlertStatus) {
          processAlertStatus = tmp.processAlertStatus
        }
      }
    }
  }

  if (processAlertStatus >= 30) {
    return 'red'
  }
  return ''
}

const getTravelDetailStage = (stageIndex: number) => {
  return getTravelDetail('stage', stageIndex)
}

const getTravelDetailTask = (stageIndex: number, taskIndex: number) => {
  return getTravelDetail('task', stageIndex, taskIndex)
}

const getWarningStage = (stageIndex: number) => {
  return getWarning('stage', stageIndex)
}

const getWarningTask = (stageIndex: number, taskIndex: number) => {
  return getWarning('task', stageIndex, taskIndex)
}

const getTimeConditions = (timeConditions: { [key: string]: any }[], type: 'START' | 'END') => {
  const values = [] as { [key: string]: any }[]

  timeConditions.forEach((item: { [key: string]: any }) => {
    let textType = ''
    let textTimeType = ''
    const time = $formatters.datetime(item.time, 'DD/MM/YYYY HH:mm')
    if (item.type === 'START') {
      textType = 'Bắt đầu'
    }

    if (item.type === 'END') {
      textType = 'Kết thúc'
    }

    if (item.timeType === 'EARLIEST') {
      textTimeType = 'sớm nhất'
    }

    if (item.timeType === 'LATEST') {
      textTimeType = 'muộn nhất'
    }

    if (type === item.type && textType && textTimeType && time) {
      values.push({
        label: `${textType} ${textTimeType} ${time}`,
        timeType: item.timeType
      })
    }
  })

  return values
}

const getVehicle = computed(() => {
  const vehicle = {
    index: '',
    status: ''
  } as { [key: string]: any }

  // chuưa khơởi hành
  if (journey.value.processStatus === 0) {
    return vehicle
  }

  if (journey.value?.startAtLocationId) {
    vehicle.index = 'start'
    vehicle.status = 'moving'
  }

  const travelStageIndex = journey.value.travelStageIndex

  let stage = travelDetails.value.find((item: any) => item.stageIndex === travelStageIndex && item.type === 'stage')

  if (!stage) {
    if (!journey.value.stopAtActual) {
      vehicle.index = 'end'
      vehicle.status = 'moving'
    } else {
      return {}
    }
    return vehicle
  }

  if (!stage?.ata) {
    stage = getTravelDetailStage(stage?.index - 1)
  }

  if (stage) {
    vehicle.index = stage.index
    vehicle.status = stage.atd ? 'moving' : 'in-stage'
  }

  if (stage?.index === journey.value.stages.length - 1 && stage.atd && !journey.value.stopAtActual) {
    vehicle.index = 'end'
    vehicle.status = 'moving'
  }

  return vehicle
})

const CStages = computed(() => {
  const value = [] as { [key: string]: any }[]
  let valueStageIndex = 0
  const vehicle = getVehicle.value

  let vehiclePassed = true
  const stageIdsHaveDocument = stagesHaveDocument.value

  if (journey.value?.startAtLocationId) {
    value.push(cStartAtLocation.value)

    if (vehicle.index === 'start') {
      vehiclePassed = false
      value.push({
        stepType: 'vehicle',
        vehiclePassed
      })
    }
  }

  // stage
  journey.value.stages.forEach((j: any, stageIndex: number) => {
    const data = $lodash.cloneDeep(j)
    data.stepType = 'node'
    data.nodeLocationName += ` - Điểm ${stageIndex + 1}`
    data.warning = getWarningStage(stageIndex)
    data.haveDocument = stageIdsHaveDocument.includes(j._id)

    const travelData = getTravelDetailStage(stageIndex)
    if (travelData) {
      const ata = travelData.ata || travelData.ataEstimate
      data.time = $formatters.datetime(ata, 'HH:mm')
      data.day = $formatters.datetime(ata, 'DD/MM/YYYY')
      if (!travelData.ata) {
        data.estimate = true
      }
      if (travelData.atd) {
        data.disable = true
      } else {
        vehiclePassed = false
      }

      data.waitingTime = travelData.waitingTime || 0
    }
    data.vehiclePassed = vehiclePassed
    value.push(data)
    valueStageIndex = value.length - 1

    if (nodeShowTasks.value.includes(j._id)) {
      const tasks = j.tasks
      if (travelData.ata) {
        vehiclePassed = true
      }
      value[valueStageIndex].showTask = true
      value[valueStageIndex].vehiclePassed = vehiclePassed
      tasks.forEach((t: any, taskIndex: number) => {
        const tIn = $lodash.cloneDeep(t)
        const tOut = $lodash.cloneDeep(t)
        tIn.stepType = 'task'
        tOut.stepType = 'task'

        const travelDataTask = getTravelDetailTask(stageIndex, taskIndex)

        // task bắt đầu - kết thúc
        if (travelDataTask) {
          // bắt đầu
          const ata = travelDataTask.ata || travelDataTask.ataEstimate
          tIn.time = $formatters.datetime(ata, 'HH:mm')
          tIn.day = $formatters.datetime(ata, 'DD/MM/YYYY')
          tIn.warning = getWarningTask(stageIndex, taskIndex)
          tIn.timeConditions = getTimeConditions(t.timeConditions, 'START')

          if (!travelDataTask.ata) {
            tIn.estimate = true
          }

          if (travelDataTask.ata) {
            tIn.disable = true
          } else {
            vehiclePassed = false
          }

          tIn.vehiclePassed = vehiclePassed
          tIn.name = `Bắt đầu ${t.taskName}`
          tIn.waitingTime = travelDataTask.waitingTime || 0

          value.push(tIn)
          // kết thúc

          const atd = travelDataTask.atd || travelDataTask.atdEstimate
          tOut.time = $formatters.datetime(atd, 'HH:mm')
          tOut.day = $formatters.datetime(atd, 'DD/MM/YYYY')
          tOut.warning = getWarningTask(stageIndex, taskIndex)
          tOut.timeConditions = getTimeConditions(t.timeConditions, 'END')

          if (!travelDataTask.atd) {
            tOut.estimate = true
          }

          if (travelDataTask.atd) {
            tOut.disable = true
          }

          if (!travelDataTask.atd || !(tasks[taskIndex + 1]?.ata || travelData.atd)) {
            vehiclePassed = false
          }

          tOut.vehiclePassed = vehiclePassed
          tOut.name = `Kết thúc ${t.taskName}`
          value.push(tOut)
        }
      })
    }

    if (vehicle.index === stageIndex) {
      if (vehicle.status === 'moving') {
        value.push({
          stepType: 'vehicle'
        })
      }
      if (vehicle.status === 'in-stage') {
        value[valueStageIndex].vehicle = true
      }
    }
  })

  if (journey.value?.stopAtLocationId) {
    if (vehicle.index === 'end') {
      vehiclePassed = false
      value.push({
        stepType: 'vehicle',
        vehiclePassed
      })
    }
    value.push(cStopAtLocation.value)
  }

  return value
})

const clickStep = (value: any) => {
  if (!value?._id) {
    return
  }

  if (nodeShowTasks.value.includes(value._id)) {
    nodeShowTasks.value = nodeShowTasks.value.filter((id) => id !== value._id)
    return
  }
  nodeShowTasks.value.push(value._id)
}

const handleEditPlan = (tab: string) => {
  if (tab === 'plan') {
    emits('modify')
  }
}
</script>
